import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Flex } from "@radix-ui/themes";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { useCallback } from "react";
import z from "zod";
import { useNoticeApi } from "@/v2/entities/notice/Notice.api";
import { useQuery } from "react-query";
import { NoticeListView } from "@/v2/features/notice/NoticeListView";
import { NoticesPageHeader } from "@/v2/features/notice/NoticesPageHeader";

export function useNoticeId() {
  const { noticeId } = useParams();
  return z.string().optional().parse(noticeId);
}

function NoticesPage() {
  const navigate = useNavigate();
  const selectedNoticeId = useNoticeId();

  const { getNotices } = useNoticeApi();

  const { data: notices, refetch: refetchNotices } = useQuery({
    queryKey: ["notices"],
    queryFn: getNotices,
    select: ({ notices }) => notices,
  });

  const handleSelectNotice = useCallback(
    (noticeId: string) => {
      if (selectedNoticeId === noticeId) {
        navigate("/notices");
        return;
      }

      navigate(`/notices/${noticeId}`, { replace: true });
    },
    [navigate, selectedNoticeId]
  );

  const handleOnCreateNoticeSuccess = useCallback(
    async (noticeId: string) => {
      await refetchNotices();
      handleSelectNotice(noticeId);
    },
    [refetchNotices, handleSelectNotice]
  );

  const handleRefetchNotices = useCallback(async () => {
    await refetchNotices();
  }, [refetchNotices]);

  return (
    <PageLayoutV2>
      <Flex height="100%" position="relative">
        <Flex
          flexShrink={"1"}
          width={selectedNoticeId ? "" : "100%"}
          minWidth="400px"
          direction="column"
          className={selectedNoticeId ? "border-r" : ""}
        >
          <NoticesPageHeader
            onCreateNoticeSuccess={handleOnCreateNoticeSuccess}
          />

          <Flex px="20px" flexGrow="1" direction="column">
            <NoticeListView
              notices={notices}
              selectedNoticeId={selectedNoticeId}
              onSelect={handleSelectNotice}
              onRefetch={handleRefetchNotices}
            />
          </Flex>
        </Flex>

        <Flex flexGrow="1" width={selectedNoticeId ? "100%" : "0"}>
          <Outlet />
        </Flex>
      </Flex>
    </PageLayoutV2>
  );
}

export default NoticesPage;
